import { useEffect, useState } from "react";
import axios from "axios";
import BoardWorkspace from "@/components/BoardWorkspace";
import { useAuth } from "@/auth/AuthContext";
import "@/styles/data-transfer.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/settings/queue-timing`;
const errorText = (failure) => {
  const detail = failure.response?.data?.detail;
  return typeof detail === "string" ? detail : "Queue timing could not be saved. Check the values and try again.";
};

const LOCATE_API = `${API}/locates`;
const LOCATE_FIELDS = [
  ["locate_warning_days", "811 locate expiring warning (days)", "Days before a 811 ticket expires that it is flagged as expiring and appears in the Action Queue."],
  ["water_wait_business_days", "Water locate waiting period (business days)", "Business days (Mon–Fri) after a water locate is submitted before work may start."],
  ["water_warning_days", "Water locate expiration warning (days)", "Days before a submitted water locate expires that it appears in the Action Queue."],
];

function LocateTimingForm({ canEdit }) {
  const [settings, setSettings] = useState(null);
  const [values, setValues] = useState({});
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const apply = (data) => { setSettings(data); setValues(Object.fromEntries(LOCATE_FIELDS.map(([key]) => [key, String(data[key])]))); };
  useEffect(() => { axios.get(LOCATE_API).then((response) => apply(response.data)).catch((failure) => setError(errorText(failure))); }, []);
  const save = async (event) => {
    event.preventDefault(); setError(""); setMessage(""); setSaving(true);
    try {
      const response = await axios.put(LOCATE_API, Object.fromEntries(LOCATE_FIELDS.map(([key]) => [key, Number(values[key])])));
      apply(response.data); setMessage("Locate timing saved.");
    } catch (failure) { setError(errorText(failure)); } finally { setSaving(false); }
  };
  return <section className="data-card" aria-labelledby="locate-timing-heading">
    <h2 id="locate-timing-heading">811 &amp; water locate timing</h2>
    {!settings && !error && <p>Loading…</p>}
    {settings && <form onSubmit={save}>
      {LOCATE_FIELDS.map(([key, label, help]) => <div key={key}>
        <label>{label}
          <input type="number" inputMode="numeric" step="1" min={settings.limits[key].min} max={settings.limits[key].max} value={values[key]} disabled={!canEdit} onChange={(event) => setValues({ ...values, [key]: event.target.value })} />
        </label>
        <small>{help} Whole numbers from {settings.limits[key].min} to {settings.limits[key].max}. Default {settings.defaults[key]}.</small>
      </div>)}
      {!canEdit && <p>Only a company Owner/Admin can change these values.</p>}
      {canEdit && <button type="submit" disabled={saving}>{saving ? "Saving…" : "Save locate timing"}</button>}
    </form>}
    {error && <p role="alert">{error}</p>}
    {message && <p role="status">{message}</p>}
  </section>;
}

export default function QueueTimingPage() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("operations:admin");
  const [settings, setSettings] = useState(null);
  const [wait, setWait] = useState("");
  const [expiration, setExpiration] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const apply = (data) => { setSettings(data); setWait(String(data.wait_seconds)); setExpiration(String(data.expiration_hours)); };
  useEffect(() => {
    axios.get(API).then((response) => apply(response.data)).catch((failure) => setError(errorText(failure)));
  }, []);
  const save = async (event) => {
    event.preventDefault(); setError(""); setMessage(""); setSaving(true);
    try {
      const response = await axios.put(API, { wait_seconds: Number(wait), expiration_hours: Number(expiration) });
      apply(response.data); setMessage("Queue timing saved.");
    } catch (failure) { setError(errorText(failure)); } finally { setSaving(false); }
  };
  const limits = settings?.limits;
  return <BoardWorkspace eyebrow="Company administration" title="Queue Timing" subtitle="Control export queue timing and the 811 / water locate timing used by readiness and the Action Queue.">
    <section className="data-card" aria-labelledby="queue-timing-heading">
      <h2 id="queue-timing-heading">Queue timing</h2>
      {!settings && !error && <p>Loading…</p>}
      {settings && <form onSubmit={save}>
        <label>Wait time before processing (seconds)
          <input type="number" inputMode="numeric" step="1" min={limits.wait_seconds.min} max={limits.wait_seconds.max} value={wait} disabled={!canEdit} onChange={(event) => setWait(event.target.value)} />
        </label>
        <small>Whole seconds from {limits.wait_seconds.min} to {limits.wait_seconds.max}. Default {settings.defaults.wait_seconds} (start right away). Also added to automatic retry delays.</small>
        <label>Expiration window (hours)
          <input type="number" inputMode="numeric" step="1" min={limits.expiration_hours.min} max={limits.expiration_hours.max} value={expiration} disabled={!canEdit} onChange={(event) => setExpiration(event.target.value)} />
        </label>
        <small>Whole hours from {limits.expiration_hours.min} to {limits.expiration_hours.max}. Default {settings.defaults.expiration_hours}. Applies to files that finish after you save.</small>
        {!canEdit && <p>Only a company Owner/Admin can change these values.</p>}
        {canEdit && <button type="submit" disabled={saving}>{saving ? "Saving…" : "Save queue timing"}</button>}
      </form>}
      {error && <p role="alert">{error}</p>}
      {message && <p role="status">{message}</p>}
    </section>
    <LocateTimingForm canEdit={canEdit} />
  </BoardWorkspace>;
}
